import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  InputBase,
  Typography,
  Box,
  Popover,
  FormControl,
  Button,
  ListItemButton,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ErrorMessage } from "formik";
import "./company-filter.scss";

export interface CompanyOption {
  id: number;
  company_name: string;
}

export interface AutoSelectProps {
  options: CompanyOption[];
  fieldName?: string;
  isShowError?: boolean;
  handleBlur?: () => void;
  onApplyFilter?: any;
  filters?: any;
}

const CompanyFilter: React.FC<AutoSelectProps> = ({
  options,
  fieldName,
  isShowError = false,
  handleBlur,
  onApplyFilter,
  filters,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<CompanyOption[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    handleBlur?.();
    setSearchText("");
  };

  useEffect(() => {
    if (anchorEl && inputRef.current) {
      inputRef.current.focus();
    }
  }, [anchorEl]);

  const handleToggle = (option: CompanyOption) => {
    setSelectedOptions((prev) =>
      prev.some((o) => o.id === option.id)
        ? prev.filter((o) => o.id !== option.id)
        : [...prev, option]
    );
  };

  const handleReset = () => {
    setSelectedOptions([]);
    handleClose();
    onApplyFilter({});
  };

  useEffect(() => {
    if (!filters?.company || !Array.isArray(options)) return;

    const filterCompanyIds = Array.isArray(filters.company)
      ? filters.company
      : [filters.company];

    const selected = options.filter((opt) => filterCompanyIds.includes(opt.id));

    setSelectedOptions(selected);
  }, [filters, options]);

  const handleApply = () => {
    const selectedIds = selectedOptions.map((o) => o.id);
    if (selectedIds.length === 0) {
      onApplyFilter(null);
    } else if (selectedIds.length === 1) {
      onApplyFilter({ company: selectedIds[0] });
    } else {
      onApplyFilter({ company: selectedIds });
    }
    handleClose();
  };

  const filteredOptions = useMemo(
    () =>
      options?.filter((opt) =>
        opt.company_name.toLowerCase().includes(searchText.toLowerCase())
      ),
    [options, searchText]
  );

  const isSelected = useCallback(
    (option: CompanyOption) => selectedOptions.some((o) => o.id === option.id),
    [selectedOptions]
  );

  return (
    <Box>
      <div className="user-mgmt-form-auto-select-component">
        <FormControl fullWidth error={isShowError}>
          <Box
            onClick={handleOpen}
            sx={{
              borderRadius: "4px",
              padding: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              height: "18px",
              background: "#fff",
            }}
          >
            <Typography
              sx={{
                color: selectedOptions.length ? "#000" : "#aaa",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
                maxWidth: "100%",
              }}
            >
              {selectedOptions.length
                ? selectedOptions
                    .slice(0, 2)
                    .map((o) => o.company_name)
                    .join(", ") + (selectedOptions.length > 2 ? "..." : "")
                : "Select Company"}
            </Typography>
            <ExpandMoreIcon />
          </Box>

          <Popover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              style: {
                width: anchorEl?.clientWidth,
              },
            }}
          >
            <Box sx={{ padding: "8px", position: "relative" }}>
              <SearchIcon
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "16px",
                  transform: "translateY(-50%)",
                  color: "#8D8D8D",
                  pointerEvents: "none",
                  fontSize: 20,
                  zIndex: 1,
                }}
              />
              <InputBase
                inputRef={inputRef}
                placeholder="Search Company"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                fullWidth
                sx={{
                  padding: "8px 12px 8px 40px",
                  border: "1px solid #c2bcbc",
                  borderRadius: "6px",
                  backgroundColor: "#f5f5f5",
                  fontSize: "14px",
                  height: "37px",
                  color: "#000",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              />
            </Box>

            <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const selected = isSelected(option);
                  return (
                    <ListItemButton
                      key={option.id}
                      dense
                      disableGutters
                      onClick={() => handleToggle(option)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 16px",
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {option.company_name}
                      </Typography>
                      <Checkbox
                        checked={selected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleToggle(option)}
                        sx={{
                          padding: 0,
                          "&.Mui-checked": {
                            color: "#1E3763",
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })
              ) : (
                <Typography sx={{ padding: "8px 16px", color: "#888" }}>
                  No companies found
                </Typography>
              )}
            </Box>

            {selectedOptions.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  p: 1,
                  borderTop: "1px solid #e0e0e0",
                  background: "#fafafa",
                }}
                className="company-filter-actions-buttons"
              >
                <button
                  className="custom-reset-btn-user-mgmt-company-reset"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="custom-apply-btn-user-mgmt-company-apply"
                  onClick={handleApply}
                >
                  Apply
                </button>
              </Box>
            )}
          </Popover>
        </FormControl>

        {isShowError && fieldName && (
          <ErrorMessage
            name={fieldName}
            component="div"
            className="error-message"
          />
        )}
      </div>
    </Box>
  );
};

export default CompanyFilter;
